import { faPen, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLayoutEffect, useRef, useState } from 'react';

import { BlurredLineupsCard } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { RotationPlayerInfo } from '@/entities/rotations/RotationsInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useDeleteAllTieBreakerRotationsMutation } from '@/hooks/tie-breaker/useDeleteAllTieBreakerRotationsMutation';
import { useTeamEditParams } from '@/hooks/useTeamEditParams';
import { shouldBlurTieBreakerLineup } from '@/infrastructure/utils/lineup';
import { useTiebreakerStore } from '@/store/tie-breaker-store';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface RotationPlayerListProps {
	rotations: RotationPlayerInfo[];
	showBothPlayers?: boolean;
	title?: React.ReactNode;
	isAwayTeam?: boolean;
	teamTitle: string;
	tieBreakerMatchUuid: string;
	shouldShowTieBreakerCard?: boolean;
	canManage?: boolean;
	onHeightChange?: (height: number) => void;
	tieBreakerLineupPublishedDate?: string | null;
}

export const RotationPlayerList = ({
	title,
	rotations,
	showBothPlayers = true,
	isAwayTeam,
	teamTitle,
	tieBreakerMatchUuid,
	shouldShowTieBreakerCard,
	canManage,
	onHeightChange,
	tieBreakerLineupPublishedDate = null
}: RotationPlayerListProps) => {
	const pathname = usePathname();
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);
	const pathParts = pathname.split('/');
	const isTieBreakerLineupSelectionRoute = pathname.includes('/tie-breaker');

	const { isEditMode: isEditModeParam } = useTeamEditParams();

	const teamLeagueId = pathParts[2];
	const matchupId = pathParts[4];
	const [isDeleteRotationModalOpen, setIsDeleteRotationModalOpen] = useState(false);
	// pass tie breaker match uuid
	const deleteTieBreakerRotations = useDeleteAllTieBreakerRotationsMutation(tieBreakerMatchUuid);
	const tieBreakerStore = useTiebreakerStore();

	const cardRef = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		if (!cardRef.current) return;

		const height = cardRef.current.offsetHeight;
		onHeightChange?.(height);
	}, [onHeightChange]);

	if (!rotations.length) {
		return null;
	}

	const visibility = tieBreakerStore.tieBreakerLineupVisibility;

	const hasLineupAccess = Boolean(canManage || isTieBreakerLineupSelectionRoute || shouldShowTieBreakerCard);

	const shouldBlurLineup = shouldBlurTieBreakerLineup(visibility, tieBreakerLineupPublishedDate, hasLineupAccess);

	return (
		<div ref={cardRef}>
			<div className="w-full max-w-[420px] min-w-[320px] lg:min-w-[420px] p-2 flex flex-col rounded-3xl border border-gray-300 bg-white">
				<div className="max-h-[350px] overflow-y-auto p-2">
					{canManage ? (
						<div className="flex flex-row items-end lg:items-start gap-2 justify-end pb-[6px]">
							{!isEditModeParam ? (
								<>
									<Button
										variant="outline"
										className="h-7 flex items-center gap-1 px-2 py-1"
										onClick={() => {
											setIsDeleteRotationModalOpen(true);
										}}
									>
										<p className="text-xs text-error-500 font-semibold">Remove Lineup</p>
										<FontAwesomeIcon icon={faTrashAlt} className="size-4 text-error-500" />
									</Button>

									<Link
										href={`/team-leagues/${teamLeagueId}/matchup/${matchupId}/tie-breaker?mode=edit&team=${isAwayTeam ? 'away' : 'home'}`}
										prefetch={false}
										className="text-darkorange-400 font-semibold cursor-pointer"
									>
										<Button variant="outline" className="h-7 flex items-center gap-1 px-2 py-1">
											<p className="text-xs text-gray-800 font-semibold">Edit</p>
											<FontAwesomeIcon icon={faPen} className="size-4 text-gray-800" />
										</Button>
									</Link>
								</>
							) : null}
						</div>
					) : null}

					<div className="flex flex-row items-center gap-4 justify-between">{title}</div>

					{shouldBlurLineup ? (
						<BlurredLineupsCard rotations={rotations} showBothPlayers={showBothPlayers} />
					) : (
						<div className="flex flex-col gap-4 max-w-full">
							{rotations?.map((player, index) => (
								<div key={player.playerOneUuid} className="flex items-center gap-3">
									<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 text-sm">
										{index + 1}
									</span>

									{showBothPlayers ? (
										<div className="flex flex-col w-full break-words leading-tight">
											<span>{player.playerOneName}</span>
											<span>{player?.playerTwoName}</span>
										</div>
									) : (
										<span className="w-full break-words leading-tight">{player.playerOneName}</span>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{isDeleteRotationModalOpen ? (
				<ModalWrapper
					title={`Remove ${labels.tieBreakerMatchTitle} lineup`}
					onClose={() => {
						setIsDeleteRotationModalOpen(false);
					}}
					closeOnOutsideClick
					showModalHeader
					className="max-w-[430px] !max-h-[655px]"
				>
					<p className="text-orange-500 font-semibold text-lg pt-4">
						{teamTitle} - {isAwayTeam ? 'Away' : 'Home'}
					</p>
					<p className="text-gray-500 text-base font-medium pt-3">Are you sure you want to remove this lineup?</p>
					<div className="flex gap-4 w-full justify-end pt-4">
						<Button
							className={`h-10 text-sm font-semibold leading-5`}
							onClick={() => setIsDeleteRotationModalOpen(false)}
							disabled={deleteTieBreakerRotations.isDeleting}
							variant="outline"
						>
							Cancel
						</Button>
						<Button
							type="submit"
							onClick={() => {
								deleteTieBreakerRotations.deleteAllRotations.mutate(isAwayTeam ? '2' : '1');
							}}
							className={`h-10 text-sm font-semibold leading-5 disabled:cursor-not-allowed`}
							disabled={deleteTieBreakerRotations.isDeleting}
						>
							Remove
						</Button>
					</div>
				</ModalWrapper>
			) : null}
		</div>
	);
};
